import React from "react";
import {CSSObject} from "@core-ui/react-stitch";
import {kebabCase} from "@core-ui/utility";
import {transitions} from "./transitions";
import {Transition, getTransitionDurations} from "./Transition";
import {FadeProps} from "./types";

const makeTransition = (
  transition: CSSObject,
  duration?: number,
  delay = 0
): CSSObject => {
  const transitionDuration = duration
    ? `${duration}ms`
    : transition.transitionDuration;
  return {
    ...transition,
    transitionDuration,
    transitionDelay: `${delay}ms`,
  };
};

export function Fade<FadeProperty extends keyof CSSObject>({
  children,
  delay,
  duration,
  fadeProperty,
  ...props
}: FadeProps<FadeProperty>): JSX.Element {
  const enterTransition = makeTransition(
    transitions.expressive.in,
    duration?.enter,
    delay?.enter
  );

  const exitTransition = makeTransition(
    transitions.performance.out,
    duration?.exit,
    delay?.exit
  );

  const defaultFadeProperty: FadeProps<"opacity">["fadeProperty"] = {
    property: "opacity",
    start: 0,
    end: 1,
  };

  const {property, start, end} = fadeProperty ?? defaultFadeProperty;

  const startFadeProperty = {[property]: start};
  const endFadeProperty = {[property]: end};

  const baseFadeStyles: CSSObject = {
    transitionProperty: kebabCase(property as string),
    ...enterTransition,
    ...startFadeProperty,
  };

  const fadeStyles = {
    entering: endFadeProperty,
    entered: endFadeProperty,
    exiting: exitTransition,
    exited: {},
    unmounted: {},
  };

  return (
    <Transition
      {...props}
      timeout={getTransitionDurations(enterTransition, exitTransition)}
    >
      {(transitionStatus) =>
        children({
          ...baseFadeStyles,
          ...fadeStyles[transitionStatus],
        })
      }
    </Transition>
  );
}
